export const typography = {
  // Shared font families
  fonts: {
    sans: 'var(--font-sans)',
    mono: 'var(--font-mono)',
  },

  // Marketing typography scale - larger, more expressive
  marketing: {
    hero: {
      size: 'text-5xl sm:text-6xl md:text-7xl lg:text-8xl',
      weight: 'font-bold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    display: {
      size: 'text-4xl sm:text-5xl md:text-6xl',
      weight: 'font-bold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    heading1: {
      size: 'text-3xl sm:text-4xl md:text-5xl',
      weight: 'font-bold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    heading2: {
      size: 'text-2xl sm:text-3xl md:text-4xl',
      weight: 'font-semibold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    heading3: {
      size: 'text-xl sm:text-2xl md:text-3xl',
      weight: 'font-semibold',
      tracking: 'tracking-tight',
      leading: 'leading-snug',
    },
    heading4: {
      size: 'text-lg sm:text-xl md:text-2xl',
      weight: 'font-semibold',
      tracking: 'tracking-tight',
      leading: 'leading-snug',
    },
    subtitle: {
      size: 'text-lg sm:text-xl',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-relaxed',
    },
    body: {
      size: 'text-base sm:text-lg',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-relaxed',
    },
    small: {
      size: 'text-sm sm:text-base',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-relaxed',
    },
    caption: {
      size: 'text-xs sm:text-sm',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-relaxed',
    },
  },

  // Dashboard typography scale - compact, utility-focused
  dashboard: {
    pageTitle: {
      size: 'text-2xl',
      weight: 'font-semibold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    sectionTitle: {
      size: 'text-xl',
      weight: 'font-semibold',
      tracking: 'tracking-tight',
      leading: 'leading-tight',
    },
    cardTitle: {
      size: 'text-lg',
      weight: 'font-semibold',
      tracking: 'tracking-normal',
      leading: 'leading-snug',
    },
    heading: {
      size: 'text-base',
      weight: 'font-semibold',
      tracking: 'tracking-normal',
      leading: 'leading-snug',
    },
    label: {
      size: 'text-sm',
      weight: 'font-medium',
      tracking: 'tracking-normal',
      leading: 'leading-snug',
    },
    body: {
      size: 'text-sm',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-normal',
    },
    small: {
      size: 'text-xs',
      weight: 'font-normal',
      tracking: 'tracking-normal',
      leading: 'leading-normal',
    },
    caption: {
      size: 'text-xs',
      weight: 'font-normal',
      tracking: 'tracking-wide',
      leading: 'leading-normal',
    },
    uppercase: {
      size: 'text-xs',
      weight: 'font-semibold',
      tracking: 'tracking-wide',
      leading: 'leading-normal',
      transform: 'uppercase',
    },
  },

  // Shared text colors
  colors: {
    primary: 'text-foreground',
    secondary: 'text-muted-foreground',
    tertiary: 'text-muted-foreground/60',
    accent: 'text-primary',
    error: 'text-destructive',
    success: 'text-green-600 dark:text-green-500',
    warning: 'text-yellow-600 dark:text-yellow-500',
  },
} as const

// Type for style objects
interface TypographyStyle {
  size: string
  weight: string
  tracking: string
  leading: string
  transform?: string
}

// Helper function to combine typography styles
export function getTypographyClasses(
  scale: 'marketing' | 'dashboard',
  variant: MarketingTypographyVariant | DashboardTypographyVariant,
  color?: keyof typeof typography.colors
): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scaleStyles = typography[scale] as Record<string, TypographyStyle>
  const styles = scaleStyles[variant]
  
  if (!styles) {
    return ''
  }
  
  const classes = [
    styles.size,
    styles.weight,
    styles.tracking,
    styles.leading,
    styles.transform,
  ].filter(Boolean)

  if (color) {
    classes.push(typography.colors[color])
  }

  return classes.join(' ')
}

// Type exports for TypeScript support
export type MarketingTypographyVariant = keyof typeof typography.marketing
export type DashboardTypographyVariant = keyof typeof typography.dashboard
export type TypographyColor = keyof typeof typography.colors
